import { PutRequest, UploadAbortedError, UploadNetworkError, UploadResponse, UploadTransport, UploadUrlRejectedError } from './media-resume';

/** No sign of life (a progress event, an answer) from a request for this long: the connection is taken for dead (a laptop that slept, a network that changed without a reset). */
export const STALL_MS = 60_000;

/** The only place a file may be sent to: the resumable sessions of Google Cloud Storage. */
export const UPLOAD_ORIGIN = 'https://storage.googleapis.com';
export const UPLOAD_PATH_PREFIX = '/upload/storage/v1/b/';

/**
 * Is this an address the page may PUT a file to? `https:` and the origin exactly `https://storage.googleapis.com` (no other host or port, no user name in front of it), with a
 * path of the upload API of the cloud (`/upload/storage/v1/b/...`). The address comes from our own server, but the page sends the whole file (and the answer of the
 * cloud) to whatever it names, so it is checked before every request, the first and each one of the recovery. The path is read after the browser has resolved `..` and
 * `%2e%2e` in it. Anything that is not an absolute address is refused.
 */
export function isAllowedUploadUrl(url: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  return parsed.protocol === 'https:' && parsed.origin === UPLOAD_ORIGIN && parsed.username === '' && parsed.password === '' && parsed.pathname.startsWith(UPLOAD_PATH_PREFIX);
}

export interface XhrTransportOptions {
  /** How long a request may be silent before it is given up as a network error (default `STALL_MS`). */
  stallMs?: number;
  /** Makes the request object; a test hands in a fake. */
  create?: () => XMLHttpRequest;
}

/**
 * The transport of the upload: ONE `XMLHttpRequest` per `PUT`, with the progress of the body. It is not `HttpClient` on purpose: the address belongs to the cloud, the
 * app's interceptors (the bearer token, the language) must not touch it, and `HttpClient` has no clean way to read the 308 of the resumable protocol.
 * A 308 without a `Location` is an ordinary answer for `XMLHttpRequest` (the browser follows a redirect only when there is one).
 *
 * A watchdog stands over every request: a half-open connection gives neither an answer nor an error, so the request is aborted after `stallMs` without a progress event and
 * rejects as a network error, which the resumable algorithm answers by asking the cloud how much arrived.
 * An address that is not the cloud's (`isAllowedUploadUrl`) is refused before a request is made: it rejects with `UploadUrlRejectedError`, which the recovery does not retry.
 * Nothing here logs or puts the address in a message.
 */
export class XhrTransport implements UploadTransport {
  private readonly stallMs: number;
  private readonly create: () => XMLHttpRequest;

  constructor(options: XhrTransportOptions = {}) {
    this.stallMs = options.stallMs ?? STALL_MS;
    this.create = options.create ?? ((): XMLHttpRequest => new XMLHttpRequest());
  }

  put(request: PutRequest): Promise<UploadResponse> {
    return new Promise<UploadResponse>((resolve, reject) => {
      if (!isAllowedUploadUrl(request.url)) {
        reject(new UploadUrlRejectedError());
        return;
      }
      if (request.signal?.aborted) {
        reject(new UploadAbortedError());
        return;
      }
      const xhr = this.create();
      let stalled = false;
      let watchdog: ReturnType<typeof setTimeout> | null = null;
      const onAbort = (): void => xhr.abort();
      const stop = (): void => {
        if (watchdog !== null) {
          clearTimeout(watchdog);
          watchdog = null;
        }
      };
      const cleanup = (): void => {
        stop();
        request.signal?.removeEventListener('abort', onAbort);
      };
      /** Starts the silence again: called when the request starts and at every sign of life. */
      const alive = (): void => {
        stop();
        watchdog = setTimeout(() => {
          stalled = true;
          xhr.abort();
        }, this.stallMs);
      };
      xhr.open('PUT', request.url);
      for (const [name, value] of Object.entries(request.headers)) {
        xhr.setRequestHeader(name, value);
      }
      const report = request.onProgress;
      xhr.upload.onprogress = event => {
        alive();
        report?.(event.loaded);
      };
      xhr.onprogress = alive;
      xhr.onload = () => {
        cleanup();
        resolve({ status: xhr.status, header: name => xhr.getResponseHeader(name) });
      };
      xhr.onerror = () => {
        cleanup();
        reject(new UploadNetworkError());
      };
      xhr.ontimeout = () => {
        cleanup();
        reject(new UploadNetworkError());
      };
      xhr.onabort = () => {
        cleanup();
        // the watchdog aborted it (the line is dead): a network error, which is recovered; the editor or the page aborting is an abort
        reject(stalled ? new UploadNetworkError() : new UploadAbortedError());
      };
      request.signal?.addEventListener('abort', onAbort, { once: true });
      alive();
      xhr.send(request.body);
    });
  }
}
